import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { onAuthStateChanged, type User as FirebaseUser } from 'firebase/auth'
import { AlertCircle, CheckCircle2, ExternalLink, Instagram, Link as LinkIcon, LogIn, MapPin, Ticket, UserRound } from 'lucide-react'
import GuestUpgradeCard from '../components/GuestUpgradeCard'
import StampProgress from '../components/StampProgress'
import StampSuccessAnimation from '../components/StampSuccessAnimation'
import { auth } from '../lib/firebase'
import { isWeb3Mode } from '../lib/authMode'
import { getGuestId, getOrCreateGuestId } from '../lib/guestId'
import { useGeolocation } from '../hooks/useGeolocation'
import { useI18n } from '../i18n/useI18n'
import { api, ApiError, type StampBoothLink, type StampScanResult } from '../services/api'
import LotteryDrawReveal from '../components/LotteryDrawReveal'
import { useStore } from '../store/useStore'
import { useStampRally } from '../store/useStampRally'

const POST_LOGIN_REDIRECT_KEY = 'postLoginRedirect'

type Phase =
  | { kind: 'resolving' }        // auth 解決 + /stamp/scan 呼び出し中
  | { kind: 'success'; result: StampScanResult }
  | { kind: 'already'; result: StampScanResult }
  | { kind: 'needs-login'; result: StampScanResult }
  | { kind: 'geofence'; result: StampScanResult }
  | { kind: 'invalid' }
  | { kind: 'closed' }
  | { kind: 'error' }

/**
 * /stamp?t=<token> — スタンプQRの受け口 (#1126)。
 *
 * 外部カメラ（標準カメラアプリ→ブラウザ）とアプリ内スキャナ（QRScan.tsx）の
 * 両経路がここに収束する。auth 解決は Pay.tsx の移植だが、Pay と違い
 * **ログインを強制しない**: 未ログインでもサーバは店リンクを返し（匿名スキャン
 * として集客カウント）、スタンプが欲しい人だけ postLoginRedirect でログイン後に
 * この URL へ自動復帰する（Login.tsx の既存消費ロジック、変更不要）。
 */
export default function StampGate() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { t } = useI18n()
  const token = searchParams.get('t') ?? ''

  const isLoggedIn = useStore((s) => s.isLoggedIn)
  const restoreSession = useStore((s) => s.restoreSession)
  const login = useStore((s) => s.login)
  const applyScanResult = useStampRally((s) => s.applyScanResult)
  const { coords } = useGeolocation() // キャッシュ/デモ偽装のみ利用（ここでは新規プロンプトを出さない）

  const [web3Mode] = useState(isWeb3Mode)
  const [authResolved, setAuthResolved] = useState(web3Mode)
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null)
  const [phase, setPhase] = useState<Phase>({ kind: 'resolving' })
  const restoreStartedRef = useRef(false)
  const scannedRef = useRef(false) // StrictMode の二重 scan 防止 (Pay.tsx L31 と同型)

  // ---- auth 解決 (Pay.tsx L33-72 移植) ----
  useEffect(() => {
    if (web3Mode) return
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setFirebaseUser(user)
      setAuthResolved(true)
    })
    return unsubscribe
  }, [web3Mode])

  useEffect(() => {
    if (!authResolved || isLoggedIn || restoreStartedRef.current) return
    const hasLocalSession = localStorage.getItem('isLoggedIn') === 'true'
    if (hasLocalSession) {
      restoreStartedRef.current = true
      restoreSession().catch(() => {
        // セッション復元失敗 → 匿名としてスキャンに進む (scan effect 側で判定)
        restoreStartedRef.current = false
      })
      return
    }
    if (firebaseUser) {
      restoreStartedRef.current = true
      login({
        name: firebaseUser.displayName || 'User',
        email: firebaseUser.email || '',
      }).catch(() => {
        restoreStartedRef.current = false
      })
    }
  }, [authResolved, isLoggedIn, firebaseUser, restoreSession, login])

  // #1870 ゲストで参加している人。**アカウントを持っていない**ことが条件なので、
  // firebaseUser / isLoggedIn の両方を見る (StampGate は未ログインでも開く)。
  const isGuest = !isLoggedIn && !firebaseUser && getGuestId() !== null
  // #2111 この読み取りでゲストにしたか (「ゲストとして参加しました」の出し分け)。
  // 発行は effect の中、表示は描画時なので ref と state の両方を持つ
  // (描画中に ref は読めない — react-hooks/refs)。
  const autoJoinedRef = useRef(false)
  const [autoJoined, setAutoJoined] = useState(false)

  // 「ログイン処理がまだ進行しうるか」— 進行中は scan を待つ
  const authSettled =
    authResolved &&
    (isLoggedIn ||
      (!firebaseUser && localStorage.getItem('isLoggedIn') !== 'true'))

  // ---- scan 実行 (auth 確定後に1回だけ) ----
  useEffect(() => {
    if (!token || !authSettled || scannedRef.current) return
    scannedRef.current = true
    // #2111 **QR を読んだ時点で参加者。** ゲスト ID は「ゲストで参加」を押した人にしか
    // 発行していなかったため、QR から入った人・標準カメラで別のブラウザが開いた人は
    // サーバから見て匿名になり、読むたびにログインを求められていた (当日に発生)。
    // ここで発行して、ログインを挟まずスタンプが付くようにする。
    // 抽選はアカウント限定のまま (サーバがゲストを弾く) なので、当選枠は動かない。
    if (!isLoggedIn && !firebaseUser && getGuestId() === null) {
      getOrCreateGuestId()
      // **保存できたときだけ**参加したことにする。localStorage が使えない端末
      // (プライベートブラウズ等) では値が残らず、通信にも載らない (_client は
      // localStorage から読む)。そこで「参加しました」と出すと嘘になる
      autoJoinedRef.current = getGuestId() !== null
    }
    api
      .scanStampToken(token, coords ?? undefined)
      .then((result) => {
        if (autoJoinedRef.current) setAutoJoined(true)
        if (result.stamp) applyScanResult(result)
        if (result.result === 'stamped') setPhase({ kind: 'success', result })
        else if (result.result === 'already') setPhase({ kind: 'already', result })
        else if (result.result === 'geofence_denied') setPhase({ kind: 'geofence', result })
        else setPhase({ kind: 'needs-login', result })
      })
      .catch((e) => {
        if (e instanceof ApiError && e.status === 404) setPhase({ kind: 'invalid' })
        else if (e instanceof ApiError && e.status === 410) setPhase({ kind: 'closed' })
        else setPhase({ kind: 'error' })
      })
  }, [token, authSettled, coords, applyScanResult, isLoggedIn, firebaseUser])

  if (!token) {
    return <ErrorScreen title={t('stampRally.invalidQr')} hint={t('stampRally.invalidQrHint')} />
  }

  // ---- 描画 ----
  if (phase.kind === 'resolving') {
    return (
      <div className="min-h-screen bg-warm-white flex flex-col items-center justify-center gap-4 px-6 safe-top safe-bottom">
        <div className="h-8 w-8 rounded-full border-2 border-gray-300 border-t-yokoso-red animate-spin" />
        <p className="font-body text-sm text-text-secondary">{t('stampRally.gateVerifying')}</p>
      </div>
    )
  }

  if (phase.kind === 'invalid') {
    return <ErrorScreen title={t('stampRally.invalidQr')} hint={t('stampRally.invalidQrHint')} />
  }
  if (phase.kind === 'closed') {
    return <ErrorScreen title={t('stampRally.eventClosed')} hint={t('stampRally.eventClosedHint')} />
  }
  if (phase.kind === 'error') {
    return <ErrorScreen title={t('stampRally.scanError')} hint="" />
  }

  const { result } = phase
  const stamp = result.stamp
  const remaining = stamp ? Math.max(0, stamp.goal - stamp.stampCount) : 0

  return (
    <div className="min-h-screen bg-warm-white flex flex-col items-center px-6 pt-14 pb-10 safe-top safe-bottom">
      {/* ---- 状態ヘッダ ---- */}
      {phase.kind === 'success' && <StampSuccessAnimation />}
      {phase.kind === 'already' && (
        <div className="flex flex-col items-center animate-fade-in">
          <CheckCircle2 size={56} className="text-matcha mb-3" />
          <p className="font-body text-text-primary text-center">{t('stampRally.alreadyStamped')}</p>
        </div>
      )}
      {phase.kind === 'needs-login' && (
        <div className="flex flex-col items-center animate-fade-in">
          <LogIn size={56} className="text-navy mb-3" />
          <p className="font-body text-sm text-text-secondary text-center">{t('stampRally.loginNote')}</p>
        </div>
      )}
      {phase.kind === 'geofence' && (
        <div className="flex flex-col items-center animate-fade-in">
          <MapPin size={56} className="text-yokoso-red mb-3" />
          <p className="font-body text-text-primary text-center">{t('stampRally.geofenceDenied')}</p>
        </div>
      )}

      {/* #2111 **黙ってゲストにしない。** QR を読んだ時点で参加者として扱うので、
        * 本人に「いまゲストで参加した」と伝える。抽選のあるイベントでは、
        * アカウントが要ることもここで言う (完走まで黙っていると気付けない)。 */}
      {autoJoined && isGuest && stamp && (
        <div className="mt-4 w-full rounded-2xl bg-white px-4 py-3 shadow-card animate-fade-in">
          <div className="flex items-center gap-2">
            <UserRound size={16} className="shrink-0 text-text-tertiary" />
            <p className="font-body text-sm text-text-primary">{t('stampRally.guestAutoJoined')}</p>
          </div>
          <p className="mt-1 font-body text-[11px] leading-relaxed text-text-secondary">
            {t('stampRally.guestAutoJoinedNote')}
          </p>
          {result.lotteryAvailable && (
            <p className="mt-1 font-body text-[11px] leading-relaxed text-text-tertiary">
              {t('stampRally.joinGuestNoLottery')}
            </p>
          )}
        </div>
      )}

      {/* ---- 進捗 (会員のみ) ---- */}
      {stamp && (
        <div className="w-full mt-2">
          <StampProgress
            count={stamp.stampCount}
            goal={stamp.goal}
            justEarned={phase.kind === 'success'}
          />
          {/* #1870 **完走した瞬間がいちばんの切り替えどころ。** ここで出さないと、
            * チケット発行へ進んでスタンプ帳に戻らない人には一生届かない。
            * 抽選のあるイベントで、ゲストのときだけ。 */}
          {stamp.achieved && result.lotteryAvailable && isGuest && (
            <div className="mt-3">
              <GuestUpgradeCard completed redirectTo="/stamp-rally" />
            </div>
          )}
          {stamp.achieved ? (
            <div className="mt-3 bg-gold/10 rounded-xl px-4 py-3 text-center animate-fade-in">
              <p className="text-sm font-body font-medium text-text-primary mb-2">
                {result.prize?.status === 'sold_out'
                  ? t('stampRally.achievedSoldOut')
                  : t('stampRally.achievedBanner')}
              </p>
              {result.prize?.status !== 'sold_out' && (
                <button
                  onClick={() =>
                    // #2111 **イベントを渡す。** 渡さないとチケット画面は
                    // スタンプ帳 (context) から企画を引こうとし、QR から来た人は
                    // context が無いので TOP へ戻されていた。ここが完走直後の
                    // いちばん押される導線 (他の 4 箇所は元から渡している)。
                    navigate(`/stamp-rally/ticket?eventId=${encodeURIComponent(result.event.id)}`)
                  }
                  className="inline-flex items-center gap-2 rounded-full bg-gold text-white px-6 py-2.5 text-sm font-body font-medium tap-effect"
                >
                  <Ticket size={16} />
                  {t('stampRally.goToTicket')}
                </button>
              )}
            </div>
          ) : (
            phase.kind === 'success' && (
              <p className="mt-3 text-center text-sm font-body text-text-secondary">
                {t('stampRally.oneMore', { remaining: String(remaining) })}
              </p>
            )
          )}
          {/* #1871 抽選はスタンプ 1 個目で走る。**その場で結果を見せる。**
            * 2 個目以降のスキャンでも同じ結果が返るので、ここは毎回出してよい。 */}
          {result.lottery && (
            <div className="mt-3">
              <LotteryDrawReveal draw={result.lottery} />
            </div>
          )}
          {/* #2490 合言葉が要る抽選で、まだ入れていないので引けなかった。**ここで言わないと
            * 「スタンプは付いたのに抽選が無い」に見える。** 入力はスタンプ帳の抽選の欄で */}
          {!result.lottery && result.lotteryNeedsKeyword && (
            <div className="mt-3 rounded-2xl border border-[#06C755]/40 bg-[#06C755]/5 px-4 py-3 text-left">
              <p className="font-body text-sm font-medium text-text-primary">
                {t('stampRally.keywordScanPrompt')}
              </p>
              <button
                // 企画の枠は既定で閉じているので、開いた状態で着地させる (入力欄がすぐ見える)
                onClick={() => navigate(`/stamp-rally?event=${encodeURIComponent(result.event.id)}&open=program`)}
                className="tap-effect mt-2 inline-flex items-center gap-1.5 rounded-full bg-[#06C755] px-5 py-2 font-body text-sm font-medium text-white"
              >
                {t('stampRally.keywordScanCta')}
              </button>
            </div>
          )}
        </div>
      )}

      {/* ---- どの店で押したのか ----
          写真は独立した1枚、その下に店名・ブース番号・紹介文のカード。
          ブース詳細 (/stamp-rally/stores/:id) と同じ組み方に揃えている。
          以前は店名が文字だけで、写真は3ブロック下にあったため
          「どの店で押したのか」が一目で入ってこなかった。 */}
      {result.store.thumbnailUrl && (
        <img
          src={result.store.thumbnailUrl}
          alt=""
          className="mt-6 h-44 w-full rounded-2xl object-cover"
        />
      )}
      <div className="mt-3 w-full rounded-2xl bg-white px-4 py-3 text-left shadow-card">
        <p className="font-display text-lg text-navy">{result.store.name}</p>
        {result.store.boothNo && (
          <p className="mt-0.5 font-body text-xs text-text-tertiary">
            {t('stampRally.boothNo', { no: result.store.boothNo })}
          </p>
        )}
        {result.store.description && (
          <p className="mt-2 font-body text-sm leading-relaxed text-text-secondary">
            {result.store.description}
          </p>
        )}
      </div>

      {/* ---- 店リンク ---- */}
      <div className="w-full mt-4 flex flex-col gap-2">
        {phase.kind === 'needs-login' && (
          <button
            onClick={() => {
              sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, `/stamp?t=${encodeURIComponent(token)}`)
              navigate('/login')
            }}
            className="flex items-center justify-center gap-2 rounded-full bg-yokoso-red text-white py-3.5 text-sm font-body font-medium tap-effect"
          >
            <LogIn size={18} />
            {t('stampRally.loginToStamp')}
          </button>
        )}
        {/* 「お店のリンク」の見出しは置かない。ボタン自体が Instagram / HP と
            名乗っているので、上に一行足しても情報が増えない。 */}
        {/* ブース詳細 (StampBoothDetail) と同じ組み方に揃える。以前は塗りつぶしの
            ピル型で中央寄せだったため、Instagram の見え方が一般的な導線と違って
            落ち着かなかった。 */}
        {result.store.links.map((link, i) => (
          <a
            key={i}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between rounded-xl bg-white px-4 py-3.5 shadow-card tap-card"
          >
            <span className="flex items-center gap-3 font-body text-sm text-text-primary">
              {link.type === 'instagram' ? (
                <Instagram size={18} className="text-pink-500" />
              ) : (
                <LinkIcon size={18} className="text-navy" />
              )}
              {linkLabelFor(link, t)}
            </span>
            <ExternalLink size={16} className="text-text-tertiary" />
          </a>
        ))}
        {/* ブース詳細 (/stamp-rally/stores/:id) への導線は**置かない**。
            あちらは会場付近にいることを確認してから出す作りなので、位置情報を
            許可していない人はゲートに落ちる。QR を読んだ直後の人に、店の紹介を
            読むためだけに位置情報を要求することになる。
            そもそも詳細の中身 (写真・紹介文・リンク) はここに出したものと
            ほぼ同じなので、飛ばす価値より落ちる危険の方が大きい。 */}
        {/* 押せるものはボタンの見た目にする。文字だけだと押せると分からない */}
        {/* #1869 **リハーサル受付中のときだけ**イベントを名指しする。公開前で
            地域からは引けないので、素の /stamp-rally に戻すとスタンプ帳が
            「開催中のイベントはありません」になるため。
            公開イベントで付けてはいけない — ?event= はフロント側で表示を
            そのイベントだけに絞る印なので (#1870)、付けると AR宝探し等の
            他のブロックが消えて見え方が変わる。 */}
        <button
          onClick={() =>
            navigate(
              result.event.rehearsalOpen
                ? `/stamp-rally?event=${encodeURIComponent(result.event.id)}`
                : '/stamp-rally',
            )
          }
          className="mt-2 rounded-full border border-navy py-3.5 text-sm font-body font-medium text-navy tap-effect"
        >
          {t('stampRally.backToTop')}
        </button>
      </div>
    </div>
  )
}

function linkLabelFor(link: StampBoothLink, t: (key: string, params?: Record<string, string>) => string) {
  if (link.label) return link.label
  if (link.type === 'instagram') return t('stampRally.linkInstagram')
  if (link.type === 'hp') return t('stampRally.linkHp')
  return t('stampRally.linkOther')
}

function ErrorScreen({ title, hint }: { title: string; hint: string }) {
  const navigate = useNavigate()
  const { t } = useI18n()
  return (
    <div className="min-h-screen bg-warm-white flex flex-col items-center justify-center px-8 text-center safe-top safe-bottom">
      <AlertCircle size={48} className="text-yokoso-red mb-4" />
      <p className="font-body font-medium text-text-primary mb-1">{title}</p>
      {hint && <p className="text-xs font-body text-text-tertiary mb-6">{hint}</p>}
      <button
        onClick={() => navigate('/stamp-rally')}
        className="rounded-full border border-navy text-navy px-6 py-2.5 text-sm font-body font-medium tap-effect"
      >
        {t('stampRally.backToTop')}
      </button>
    </div>
  )
}
